/**
 * manifestToReactFlow — render a GENERATIVE-network MANIFEST (org-tree) as React Flow
 * nodes + edges. Sibling of ``aslToReactFlow`` (GENERATIVE-NETWORKS.md §visualization).
 *
 * A manifest is a pure tree: a Start node → each rootCohort agent → its ``team``
 * children, recursively, and every leaf → a Stop node. It emits ONLY the node/edge
 * SHAPE ({id, data:{label, comment, aslType}}); LAYOUT is owned by the renderer —
 * ``ManifestTreeFlow`` lays it out left→right (org-trees fan wide). This is deliberately
 * decoupled from the ASL renderer so neither can break the other.
 *
 * Node type is carried on ``data.aslType`` so a UNIVERSAL_BUILDER agent can render
 * distinctly from an AI worker (same field the ASL nodes use). ``data.reasonForSelection``
 * / live-run styling are future hooks (the tree can light up the walked path — §9.5).
 */
import {
  ASL_GRAPH_NODE_WIDTH,
  ASL_GRAPH_NODE_HEIGHT,
} from './aslToReactFlow.js';

const START_NODE_ID = '__start__';
const STOP_NODE_ID = '__stop__';

/** True when the object looks like a manifest (has a rootCohort tree). */
export function isManifest(obj) {
  const m = typeof obj === 'string' ? _tryParse(obj) : obj;
  return !!(m && typeof m === 'object' && Array.isArray(m.rootCohort));
}

function _tryParse(s) {
  try { return JSON.parse(s); } catch { return null; }
}

/**
 * @param {object|string} manifest — a manifest ({rootCohort:[...]}) or its JSON string
 * @returns {{ nodes: Array, edges: Array }}
 */
export function manifestToReactFlow(manifest) {
  const m = typeof manifest === 'string' ? _tryParse(manifest) : manifest;
  if (!isManifest(m)) return { nodes: [], edges: [] };

  const circleSize = 48;
  const nodes = [{
    id: START_NODE_ID,
    type: 'startNode',
    position: { x: 0, y: 0 },
    data: { label: 'Start', aslType: 'start' },
    style: { width: circleSize, height: circleSize },
  }];
  const edges = [];
  const seen = new Set();

  const addEdge = (source, target) => {
    const key = `${source}->${target}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ id: `e-${source}-${target}-${edges.length}`, source, target });
  };

  // Collect leaf node ids (agents with no ``team``) so we can wire them all to Stop.
  const leafIds = [];

  // Depth-first: each node → an 'agent' RF node + an edge from its parent. nodePath is
  // the id (root/api/perf) so it's stable + matches the engine's telemetry key.
  const walk = (cohort, parentId, prefix) => {
    for (const node of cohort || []) {
      if (!node || typeof node !== 'object') continue;
      const lid = String(node.logicalId || node.id || '').trim();
      if (!lid) continue;
      const path = `${prefix}/${lid}`;
      const type = String(node.type || 'AI').toUpperCase();
      nodes.push({
        id: path,
        type: 'agent',
        position: { x: 0, y: 0 },
        data: {
          label: lid,
          // Hover subtitle (same slot as the ASL Comment) = the agent's ROLE in the
          // bigger picture. Prefer ``description`` (role-in-context, what the user wants
          // to understand on hover); fall back to ``job`` (per-run action) for older
          // manifests that only carried a job.
          comment: String(node.description || node.job || '').slice(0, 160),
          // 'UNIVERSAL_BUILDER' | 'AI' — lets the renderer badge builders distinctly.
          aslType: type === 'UNIVERSAL_BUILDER' ? 'builder' : 'worker',
          // Every node runs DUAL at runtime (the Lead flexes worker↔builder per request);
          // the architect's authoring-time call is preserved as advisory display text
          // (e.g. "Builder recommended") shown under the node name. §10.5.
          capabilityRecommendation: String(node.capabilityRecommendation || '').slice(0, 40),
          // Every agent defaults to Fast (Haiku); the architect's tier pick is preserved as
          // advisory display text (e.g. "Opus recommended"). User upgrades in the workbench.
          modelRecommendation: String(node.modelRecommendation || '').slice(0, 40),
        },
        style: { width: ASL_GRAPH_NODE_WIDTH, height: ASL_GRAPH_NODE_HEIGHT },
      });
      addEdge(parentId, path);
      const team = Array.isArray(node.team) ? node.team.filter((c) => c && typeof c === 'object') : [];
      if (team.length) {
        walk(team, path, path);
      } else {
        leafIds.push(path);   // a leaf: nothing deeper → it terminates at Stop
      }
    }
  };

  walk(m.rootCohort, START_NODE_ID, 'root');

  // A Stop node closes the graph: every LEAF (an agent with no sub-team, where a branch
  // bottoms out) connects to it. The runtime reduces dynamically, but visually a bounded
  // Start→…→Stop tree reads far clearer than dangling leaves. Mirrors the ASL end node.
  const circleSizeStop = 48;
  nodes.push({
    id: STOP_NODE_ID,
    type: 'endNode',
    position: { x: 0, y: 0 },
    data: { label: 'Stop', aslType: 'end' },
    style: { width: circleSizeStop, height: circleSizeStop },
  });
  leafIds.forEach((lid) => addEdge(lid, STOP_NODE_ID));

  // Positions are assigned by the RENDERER: the standalone ManifestTreeFlow lays this
  // out left→right (org-trees fan wide). We only emit the node/edge SHAPE here; every
  // node starts at {0,0} and the renderer's dagre pass places it. (aslToReactFlow's TB
  // helper is intentionally NOT used — the manifest tree owns its own LR layout.)
  return { nodes, edges };
}

/**
 * manifestToRoster — the STABLE pre-seeded agent roster for the room's hierarchy panel.
 *
 * The room's roster used to be purely event-accreted: rows popped in as AppSync events arrived, so
 * a distributed generative run made the list JUMP/reshuffle as independent agents reported in. We
 * KNOW the whole topology up front (the release manifest), so we seed EVERY agent as a stable row
 * ``status: 'queued'`` the instant the tree is known; live events then only MUTATE status
 * (queued → running → done), never reorder. This yields a predictable, non-jumping layout.
 *
 * Rows match the reducer's ``AgentRosterRow`` shape (agentId/label/level/parentAgentId/nodePath),
 * so the live reducer roster merges onto these by agentId. Ordered depth-first (parents before
 * children), which is the same (level, then declaration order) the reducer sorts by.
 *
 * @param {object|string} manifest — a manifest ({rootCohort:[...]}) or its JSON string
 * @returns {Array<{agentId,label,level,parentAgentId,nodePath,status,isBuilder,seq}>}
 */
export function manifestToRoster(manifest) {
  const m = typeof manifest === 'string' ? _tryParse(manifest) : manifest;
  if (!isManifest(m)) return [];
  const rows = [];
  let seq = 0;

  // Seed the two ENGINE-STAGE rows (router + judge) for a cohort so they exist as ``standby`` from
  // t=0 — exactly like agent rows. Without this the Group Lead / Summarizer had NO seeded baseline:
  // their first wire event was already ``running`` (or, on replay, ``done``), so they JUMPED to done
  // instead of the visible standby → running → done arc every agent gets. ``ownerPath`` is the
  // nodePath whose cohort this stage routes/judges ('root' for the root cohort). Labels + placement
  // MIRROR ``agentRoomReducer.engineStagePlacement`` so the seeded row lines up with its live beat.
  const seedStagePair = (ownerPath, ownerAgentId, stageLevel) => {
    const isRoot = ownerPath === 'root';
    rows.push({
      agentId: `__lead__${ownerPath}`,
      label: isRoot ? 'Network Lead' : 'Group Lead',
      level: stageLevel,
      parentAgentId: ownerAgentId || undefined,
      nodePath: ownerPath,
      status: 'queued',
      isEngineStage: true,
      stageKind: 'lead',
      seq: (seq += 1),
    });
    rows.push({
      agentId: `__reducer__${ownerPath}`,
      label: isRoot ? 'Network Summarizer' : 'Summarizer',
      level: stageLevel,
      parentAgentId: ownerAgentId || undefined,
      nodePath: ownerPath,
      status: 'queued',
      isEngineStage: true,
      stageKind: 'reducer',
      seq: (seq += 1),
    });
  };

  // level: root cohort agents are level 1, their sub-team level 2, … (mirrors engine_agent
  // ``_parent_and_level_from_node_path`` so a seeded row lines up exactly with its live event).
  const walk = (cohort, parentAgentId, level, prefix) => {
    for (const node of cohort || []) {
      if (!node || typeof node !== 'object') continue;
      const lid = String(node.logicalId || node.id || '').trim();
      if (!lid) continue;
      const path = `${prefix}/${lid}`;      // full nodePath (root/A/B/C), matches engine telemetry
      rows.push({
        agentId: lid,
        label: lid,
        level,
        parentAgentId: parentAgentId || undefined,
        nodePath: path,
        status: 'queued',
        isBuilder: String(node.type || '').toUpperCase() === 'UNIVERSAL_BUILDER',
        seq: (seq += 1),
      });
      const team = Array.isArray(node.team) ? node.team.filter((c) => c && typeof c === 'object') : [];
      if (team.length) {
        // This node is a GATEWAY (owns a team) → the engine runs a Lead + Reducer over that team.
        // Seed the pair nested UNDER this node (level+1), then recurse into the team.
        seedStagePair(path, lid, level + 1);
        walk(team, lid, level + 1, path);
      }
    }
  };

  // The ROOT cohort itself gets a Lead + Reducer (Network Lead / Network Summarizer) at level 1,
  // but ONLY when there's real routing to do (2+ root agents — a single root agent is dispatched
  // directly, no Lead LLM call; mirrors engine_lead's trivial-routing shortcut).
  const rootAgents = (m.rootCohort || []).filter((n) => n && typeof n === 'object');
  if (rootAgents.length > 1) seedStagePair('root', null, 1);
  walk(m.rootCohort, null, 1, 'root');
  return rows;
}

/**
 * cappedOutAgentIds — the set of agentIds whose STAGE DEPTH exceeds the manual stage cap
 * (``stageDepthCap``), i.e. agents that are part of the org but NOT engaged this release because
 * the user limited run depth. Stage = 1-based topology level (rootCohort = Stage 1, their teams =
 * Stage 2, …). A cap of N means Stages 1..N run; anything at level > N is "capped out" (dim it).
 *
 * ``source`` may be a manifest ({rootCohort}) OR an already-built roster array (rows with a
 * ``level`` — e.g. the room's merged roster). ``cap`` of 0/absent/falsy → NO agent is capped
 * (Auto: the engine decides depth) → returns an empty Set.
 *
 * Pure + defensive so it's unit-testable and safe on partial data.
 *
 * @param {object|string|Array} source  a manifest, its JSON string, or a roster array
 * @param {number} cap                  stageDepthCap (0 = auto/none)
 * @returns {Set<string>} agentIds to render as "disabled — not in this release's run depth"
 */
export function cappedOutAgentIds(source, cap) {
  const n = Math.max(0, Number.parseInt(cap, 10) || 0);
  if (!n) return new Set();                 // Auto / no cap → nothing dimmed
  const out = new Set();
  const rows = Array.isArray(source) ? source : manifestToRoster(source);
  for (const r of rows) {
    if (!r || !r.agentId) continue;
    if (r.isEngineStage) continue;          // engine-stage rows follow their cohort, not the cap
    const lvl = Number(r.level);
    if (Number.isFinite(lvl) && lvl > n) out.add(String(r.agentId));
  }
  return out;
}
